import { APP_INITIALIZER, inject, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../environments/environment';
import { AppInitService } from './app.init';
import { ApiInterceptor } from '@core/interceptors/api.interceptor';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { MaterialModule } from '@core/modules/material.module';
import { SvgIconModule } from '@core/modules/svgIcons.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { de } from 'date-fns/locale';
import { StoreModule } from '@ngrx/store';
import { FilterPipe } from '@core/pipes/filter.pipe';
import { CommonModule, DatePipe } from '@angular/common';
import { EffectsModule } from '@ngrx/effects';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { FreezeInterceptor } from '@core/interceptors/freeze.interceptor';
import { MessengerService } from '@core/services/messenger/messenger.service';
import { InitEffects } from '@core/store/effects/init.effects';
import { MessengerSessionEffects } from '@core/store/effects/messenger-session.effects';
import { SessionAuthEffects } from '@core/store/effects/session-auth.effects';
import { ProfileInterestsEffects } from '@core/store/effects/profile-interests.effects';
import { appConfigFeature } from '@core/store/reducers/app-config.reducer';
import { bookedAppointmentsFeature } from '@core/store/reducers/appointments.reducer';
import { notificationsFeature } from '@core/store/reducers/notifications.reducer';
import { sessionAuthFeature } from '@core/store/reducers/session-auth.reducer';
import { sessionInfoFeature } from '@core/store/reducers/session-info.reducer';
import { sessionProfileFeature } from '@core/store/reducers/session-profile.reducer';
import { userFhirFeature } from '@core/store/reducers/user-fhir.reducer';
import { consentUrlsFeature } from '@core/store/reducers/consent-urls.reducer';
import { widgetsFeature } from '@core/store/reducers/widgets.reducer';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { TooltipDirectiveModule } from '@shared/directives/tooltip/tooltip-directive.module';
import { LocaleService } from '@shared/services/locale.service';
import { NativeClientService } from '@shared/services/native-client.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { messengerFeature } from '@core/store/reducers/messenger.reducer';
import { XtVerificationSdkModule } from '@xt/verification-sdk';
import { UserInfoService } from '@core/services/user-info.service';
import { TranslocoService } from '@jsverse/transloco';
import { XtDomainDataAccessModule } from '@xt/domain-data-access';
import { DefaultPageSizeService } from '@core/services/default-page-size.service';
import { CurCodeSystemService } from '@core/services/cur-code-system-service';
import { AuthService } from '@core/services/auth.service';
import { XtVerificationCdkModule } from '@xt/verification-cdk';
import { XtConsentSdkModule } from '@xt/consent-sdk';
import { ConsentUrlsService } from '@core/data/consent-urls/services/consent-urls.service';
import { XtPatientListCdkModule } from '@xt/patient-list-cdk';
import { CurUserInfoProvider } from '@core/services/cur-user-info.provider';
import { AppLoaderComponent } from '@shared/components/atoms/app-loader/app-loader.component';
import { XtPatientRepresentationDataAccessModule } from '@xt/patient-representations-sdk';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { PermissionConfig } from '@xt/core/permissions';
import { permissionConfig } from './permission-config';
import { QuestionnaireRendererModule } from '@features/questionnaire-renderer/questionnaire-renderer.module';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { FooterComponent } from '@shared/components/dashboard/footer/footer.component';
import {
  provideDropdownButtonConfig,
  SnackBarService,
  XT_CONTENT_FOOTER,
  XtUiKitModule,
} from '@xt/ui-kit';
import { XtRouterModule } from '@xt/core/router';
import { provideAppInitializer } from '@xt/core/utils';

import { AppConfigModule } from '@xt/core/app-config';
import {
  API_ERROR_VALIDATION_HANDLER,
  BACKEND_SERVICE_ERROR_HANDLER_PROVIDER,
  FHIR_ERROR_HANDLER_PROVIDER,
} from '@xt/core/api-error-handler';

import { UserInformationContextModule } from '@xt/user-context';

import {
  HTTP_INTERCEPTORS,
  HttpClientJsonpModule,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import {
  CardsConfig,
  VERIFICATION_CONFIG,
  VerificationConfigDataService,
} from './features/xt-verification/config';

import {
  CodeSystemService,
  LanguageProvider,
  XtDataAccessModule,
} from '@xt/data-access';

const init_app = (appLoadService: AppInitService) => () =>
  appLoadService.init();

const init_native = (nativeClientService: NativeClientService) => () =>
  nativeClientService.ngOnInit();

const init_localization = (localeService: LocaleService) => () =>
  localeService.init();

@NgModule({
  declarations: [AppComponent, FilterPipe],
  imports: [
    CommonModule,
    AppRoutingModule,
    EffectsModule.forRoot([InitEffects]),
    EffectsModule.forFeature([
      SessionAuthEffects,
      MessengerSessionEffects,
      ProfileInterestsEffects,
    ]),
    HttpClientJsonpModule,
    BrowserAnimationsModule,
    MaterialModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    TranslocoRootModule,
    FormsModule,
    ReactiveFormsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    StoreModule.forRoot({
      appConfig: appConfigFeature.reducer,
      sessionAuth: sessionAuthFeature.reducer,
      sessionInfo: sessionInfoFeature.reducer,
      sessionProfile: sessionProfileFeature.reducer,
      userFhir: userFhirFeature.reducer,
      widgets: widgetsFeature.reducer,
      notifications: notificationsFeature.reducer,
      bookedAppointments: bookedAppointmentsFeature.reducer,
      consentUrls: consentUrlsFeature.reducer,
    }),

    StoreModule.forFeature(appConfigFeature),
    StoreModule.forFeature(sessionAuthFeature),
    StoreModule.forFeature(sessionInfoFeature),
    StoreModule.forFeature(sessionProfileFeature),
    StoreModule.forFeature(userFhirFeature),
    StoreModule.forFeature(widgetsFeature),
    StoreModule.forFeature(consentUrlsFeature),
    StoreModule.forFeature(notificationsFeature),
    StoreModule.forFeature(sessionInfoFeature),
    StoreModule.forFeature(bookedAppointmentsFeature),
    StoreModule.forFeature(messengerFeature),
    StoreDevtoolsModule.instrument({
      maxAge: 50,
      logOnly: environment.production,
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      connectInZone: true,
    }),
    TooltipDirectiveModule,
    SvgIconModule,
    CypressDirectiveModule,
    AppConfigModule,
    XtUiKitModule,
    AppLoaderComponent,
    XtDataAccessModule.forRoot(
      AuthService,
      DefaultPageSizeService,
      LocaleService,
    ),
    XtConsentSdkModule.forRoot(AuthService, {
      consentPath: '',
      consentPathForRepresentation: '/dashboard/representations/:id/profile',
    }),
    XtPatientRepresentationDataAccessModule,
    XtRouterModule,
    QuestionnaireRendererModule,
    XtDomainDataAccessModule,
    UserInformationContextModule.forRoot(CurUserInfoProvider),
    XtVerificationSdkModule.forRoot(
      VERIFICATION_CONFIG,
      UserInfoService,
      VerificationConfigDataService,
    ),
    XtVerificationCdkModule.forRoot(CardsConfig),
    XtPatientListCdkModule.forRoot(ConsentUrlsService),
  ],
  providers: [
    MessengerService,
    AppInitService,
    FHIR_ERROR_HANDLER_PROVIDER,
    BACKEND_SERVICE_ERROR_HANDLER_PROVIDER,
    API_ERROR_VALIDATION_HANDLER,
    provideDateFnsAdapter(),
    provideAppInitializer(() => {
      inject(SnackBarService);
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideDropdownButtonConfig({
      mobileMenuDialog: false,
    }),
    {
      provide: MAT_DATE_LOCALE,
      useValue: de,
    },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    {
      provide: PermissionConfig,
      useValue: permissionConfig,
    },
    {
      provide: CodeSystemService,
      useClass: CurCodeSystemService,
    },
    {
      provide: LanguageProvider,
      useExisting: TranslocoService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: init_app,
      deps: [AppInitService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: init_localization,
      deps: [LocaleService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: init_native,
      deps: [NativeClientService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FreezeInterceptor,
      multi: true,
    },
    {
      provide: XT_CONTENT_FOOTER,
      useValue: FooterComponent,
    },
    DatePipe,
  ],
  exports: [FilterPipe, CommonModule],
  bootstrap: [AppComponent],
})
export class AppModule {}
